import React, { useState, useMemo } from "react";
import { GeofilterConstraint } from "@secondcloset/types/src/_common";
import { produce } from "immer";
import { GeofilterSelectionElement } from "@secondcloset/fulfillment-utils";
import locations from "@secondcloset/web-components/dist/lib/ca-us-provinces-states.json";

import ProvinceList from "./ProvinceList";
import ProvinceSelectionControl from "./ProvinceSelectionControl";
import { Button, ButtonGroup, ClearButton, FormContainer, Header } from "./styles";
import { Tabs, Input } from "antd";

const geofilterSection = [
  {
    country: {
      label: "Canada",
      value: "ca",
    },
    provinces: locations.provinces.map(({ region_code, region_name }) => ({
      label: region_name,
      value: region_code.toLowerCase(),
    })),
  },
  {
    country: {
      label: "United States",
      value: "us",
    },
    provinces: locations.states.map(({ region_code, region_name }) => ({
      label: region_name,
      value: region_code.toLowerCase(),
    })),
  },
];

const filterProvinceBySearch = (search: string, provinces: GeofilterSelectionElement[]) =>
  provinces.filter((p) => p.label.toLowerCase().includes(search.toLowerCase().trim()));
interface GeofilterFormProps {
  geofilterConstraints: GeofilterConstraint[];
  setGeofilterConstraints: React.Dispatch<React.SetStateAction<GeofilterConstraint[]>>;
}

const GeofilterForm: React.FC<GeofilterFormProps> = ({ geofilterConstraints, setGeofilterConstraints }) => {
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("ca");
  const selectedCountryConstraint = useMemo(() => {
    const result = geofilterConstraints.find((constraint) => constraint.country === selectedCountry);

    return result || { provinces: [] };
  }, [selectedCountry, geofilterConstraints]);

  const setConstraint = (country: string) => (selectedProvinces: string[]) => {
    const updatedConstraints = produce(geofilterConstraints, (draftConstraints) => {
      const countryIndex = draftConstraints.findIndex((constraint) => constraint.country === country);

      if (selectedProvinces.length === 0 && countryIndex >= 0) {
        // user deselected all. Flag all provinces of given country for destruction
        draftConstraints.find((c) => c.country === country)?.provinces.forEach((p) => (p._destroy = true));
      } else if (countryIndex >= 0) {
        // flag all unchecked provinces for destruction
        draftConstraints[countryIndex].provinces.forEach((p) => {
          p._destroy = !selectedProvinces.includes(p.province);
        });
        const newSelectedProvinces = selectedProvinces.filter(
          (sp) => !draftConstraints[countryIndex].provinces.find((p) => p.province === sp)
        );
        newSelectedProvinces.forEach((ns) => {
          draftConstraints[countryIndex].provinces.push({
            country: country,
            province: ns,
          });
        });
      } else {
        // new record
        draftConstraints.push({
          country: country,
          provinces: selectedProvinces.map((province) => {
            return { country: country, province: province };
          }),
        });
      }
    });

    setGeofilterConstraints(updatedConstraints);
  };

  const selectAll = () => {
    const provinces = geofilterSection.find((geofilter) => geofilter.country.value === selectedCountry)?.provinces;

    if (!provinces) return;

    setConstraint(selectedCountry)(filterProvinceBySearch(search, provinces).map((province) => province.value));
  };

  const clearProvinces = () => {
    setConstraint(selectedCountry)([]);
  };

  const renderActionButtons = () => (
    <ButtonGroup>
      <Button onClick={selectAll}>Select All Regions</Button>
      <ClearButton onClick={clearProvinces}>Deselect All</ClearButton>
    </ButtonGroup>
  );

  return (
    <FormContainer>
      <Header>Selecting Fulfillable Area</Header>
      <ProvinceSelectionControl />
      <Tabs activeKey={selectedCountry} onTabClick={setSelectedCountry} tabBarExtraContent={renderActionButtons()}>
        {geofilterSection.map((geofilter) => (
          <Tabs.TabPane tab={geofilter.country.label} key={geofilter.country.value}>
            {selectedCountry === geofilter.country.value && (
              <div>
                <Input.Search
                  style={{ width: 250, margin: ".5em 0" }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Region"
                  allowClear
                />
                <ProvinceList
                  key={geofilter.country.value}
                  provinces={filterProvinceBySearch(search, geofilter.provinces)}
                  countryProvinces={selectedCountryConstraint.provinces}
                  setConstraint={setConstraint(geofilter.country.value)}
                />
              </div>
            )}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </FormContainer>
  );
};

export default GeofilterForm;
